import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface SlotMachineTextProps {
    text: string;
    className?: string; // Parent font size/style
    delay?: number;
}

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";

const SlotReel = ({ char, index, delay = 0 }: { char: string, index: number, delay?: number }) => {
    // Initialize with a pre-filled strip to ensure it's never empty (SSR/Hydration safe-ish)
    // We start with the target char visible, then effectively "prepend" the scroll strip in the effect
    const [reelStrip, setReelStrip] = useState<string[]>([char]);
    const [isSpinning, setIsSpinning] = useState(false);

    useEffect(() => {
        // Build the full strip
        const strip = [];
        const len = 15 + Math.floor(Math.random() * 5);

        for (let i = 0; i < len; i++) {
            strip.push(ALPHABET[Math.floor(Math.random() * ALPHABET.length)]);
        }
        strip.push(char);

        setReelStrip(strip);
        setIsSpinning(true);
    }, [char]);

    // If spinning, we want to be at the END (target). 
    // Wait, the logic is: Strip = [Random...Random, Target]. 
    // We start at Y=0 (Top, first Random) and move to Y=-100% * (len-1) (Bottom, Target).
    // So initially, displaying [char] at Y=0 is correct (it shows char).
    // Then we update strip to [R, R, R, ... T].
    // If we stay at Y=0, we show the FIRST Random char. That's fine for "spinning".
    // Then we animate to Y max.

    const finalIndex = reelStrip.length - 1;

    return (
        <div className="inline-block overflow-hidden align-top relative" style={{ height: '1em', whiteSpace: 'pre' }}>
            {/* Ghost Character: Defines the natural width of the slot based on the TARGET char */}
            <div className="invisible h-full flex items-center justify-center aria-hidden">
                {char === ' ' ? '\u00A0' : char}
            </div>

            {/* The Spinning Strip */}
            <motion.div
                initial={{ y: "0em" }}
                animate={{ y: isSpinning ? `-${finalIndex}em` : "0em" }}
                transition={{
                    duration: 2.0 + (index * 0.1),
                    ease: [0.16, 1, 0.3, 1],
                    delay: delay,
                }}
                className="absolute top-0 left-0 w-full flex flex-col text-center"
            >
                {reelStrip.map((c, i) => (
                    <div key={i} style={{ height: '1em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {c === ' ' ? '\u00A0' : c}
                    </div>
                ))}
            </motion.div>
        </div>
    );
};

const SlotMachineText: React.FC<SlotMachineTextProps> = ({ text, className, delay = 0 }) => {
    return (
        <div className={`${className} flex flex-nowrap justify-center leading-none`} aria-label={text}> {/* No wrapping allowed */}
            {text.split('').map((char, i) => (
                <SlotReel
                    key={i}
                    char={char}
                    index={i}
                    delay={delay}
                />
            ))}
        </div>
    );
};

export default SlotMachineText;
